import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllOrders } from '../api/tailorApi';

// --- OrderListing Component ---

const OrderListing = () => {
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // --- Filter States ---
    const [searchTerm, setSearchTerm] = useState('');
    const [filterOrderId, setFilterOrderId] = useState(''); // 💡 State for dedicated ID filtering
    const [filterStatus, setFilterStatus] = useState('All');
    const [filterPaymentMode, setFilterPaymentMode] = useState('All');
    const [sortBy, setSortBy] = useState('order_date_desc');

    const uniqueStatuses = ['All', 'Pending', 'Completed', 'Cancelled', 'Trial', 'On Order'];
    const uniquePaymentModes = ['All', 'Cash', 'Card', 'UPI', 'Bank Transfer'];

    /**
     * Helper function to safely get a string value, returning 'Pending' or 'N/A' if null, undefined, or empty string.
     */
    const safeGetString = (value, fallback = 'N/A') => {
        if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
            return fallback;
        }
        return String(value);
    };

    const formatCurrency = (amount) => {
        const numericAmount = parseFloat(amount) || 0;
        return `₹${numericAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
    };

    const getStatusColor = (status) => {
        if (status === 'Cancelled') return 'bg-red-100 text-red-800';
        if (status === 'Completed' || status === 'Delivered') return 'bg-green-100 text-green-800';
        return 'bg-yellow-100 text-yellow-800';
    };

    // --- Data Fetching: Correctly handles NESTED 'items' array ---
    const fetchOrders = async () => {
        setLoading(true);
        setError(null);
        try {
            // NOTE: Using a mock function since getAllOrders is an external import
            const rawData = await getAllOrders();
            // console.log("Raw Data:", rawData); // For debugging API response structure

            // --- MOCK DATA to ensure visibility if API fails or for testing ---
            const mockOrders = [
                {
                    id: 1001, customer_name: 'Priya Sharma', customer_phone: '9876543210', delivery_date: '2025-12-01', total_amount: 12500, status: 'On Order', payment_mode: 'UPI', order_date: '2025-11-10',
                    items: [{ costume_name: 'Wedding Lehenga', employee_name: 'Ramesh' }]
                },
                {
                    id: 1002, customer_name: 'Amit Varma', customer_phone: '9988776655', delivery_date: '2025-11-19', total_amount: 3500, status: 'Pending', payment_mode: 'Cash', order_date: '2025-11-15',
                    items: [{ costume_name: 'Formal Shirt (x3)', employee_name: 'Suresh' }]
                },
                {
                    id: 1003, customer_name: 'Cancelled Order', customer_phone: '1234567890', delivery_date: '2025-11-20', total_amount: 7000, status: 'Cancelled', payment_mode: 'Bank Transfer', order_date: '2025-11-05',
                    items: [{ costume_name: 'Tuxedo', employee_name: 'N/A' }]
                },
            ];

            const data = Array.isArray(rawData) ? rawData : mockOrders;

            if (Array.isArray(data)) {
                const formattedOrders = data.map(order => {

                    // Extract details from the FIRST item in the array for the listing view
                    const firstItem = order.items && order.items.length > 0 ? order.items[0] : {};

                    return {
                        ...order,
                        id: safeGetString(order.id),
                        customer_name: safeGetString(order.customer_name),
                        customer_phone: safeGetString(order.customer_phone),
                        delivery_date: order.delivery_date || 'N/A',

                        // Assign nested item details to the top-level order object
                        costume_name: safeGetString(firstItem.costume_name, 'Multiple/N/A'),
                        employee_name: safeGetString(firstItem.employee_name, 'Unassigned'),

                        // Remaining top-level fields
                        status: safeGetString(order.status, 'Pending'),
                        payment_mode: safeGetString(order.payment_mode, 'N/A'),
                        order_date: order.order_date || '1970-01-01',
                        total_amount: parseFloat(order.total_amount) || 0,
                    };
                });
                setOrders(formattedOrders);
            } else {
                setError("Invalid data format received from API. Expected an array.");
                setOrders([]);
            }
        } catch (err) {
            console.error("Failed to fetch order list:", err);
            // Optionally use mock data if API fails: setOrders(mockOrders);
            setError(err.message || "Failed to load orders from API.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    // --- Memoized Filtering and Sorting Logic ---
    const filteredAndSortedOrders = useMemo(() => {
        let currentOrders = [...orders];
        const lowerFilterOrderId = filterOrderId.trim().toLowerCase();
        const lowerSearchTerm = searchTerm.trim().toLowerCase();

        // 1. Dedicated Order ID Filtering (Highest Priority)
        if (lowerFilterOrderId) {
            currentOrders = currentOrders.filter(order =>
                String(order.id).toLowerCase().includes(lowerFilterOrderId)
            );
        }

        // 2. Filtering by Dropdowns
        if (filterStatus !== 'All') {
            currentOrders = currentOrders.filter(order => order.status === filterStatus);
        }
        if (filterPaymentMode !== 'All') {
            currentOrders = currentOrders.filter(order => order.payment_mode === filterPaymentMode);
        }

        // 3. General Search Term Filtering (Excluding Order ID since it has its own filter)
        if (lowerSearchTerm) {
            currentOrders = currentOrders.filter(order =>
                order.customer_name.toLowerCase().includes(lowerSearchTerm) ||
                order.customer_phone.toLowerCase().includes(lowerSearchTerm) ||
                order.costume_name.toLowerCase().includes(lowerSearchTerm) ||
                order.employee_name.toLowerCase().includes(lowerSearchTerm) ||
                order.status.toLowerCase().includes(lowerSearchTerm) ||
                order.payment_mode.toLowerCase().includes(lowerSearchTerm)
            );
        }

        // 4. Sorting
        currentOrders.sort((a, b) => {
            const dateA = new Date(a.order_date);
            const dateB = new Date(b.order_date);

            if (sortBy === 'order_date_asc') {
                return dateA - dateB;
            } else if (sortBy === 'order_date_desc') {
                return dateB - dateA;
            }
            return 0;
        });

        return currentOrders;
    }, [orders, searchTerm, filterStatus, filterPaymentMode, sortBy, filterOrderId]);


    // --- Handlers ---
    const handleManageOrder = (orderId) => {
        navigate('/order-manager', {
            state: {
                orderIdToManage: orderId,
            }
        });
    };

    // --- Render Logic ---

    if (loading) return <div className="p-8 text-center text-xl text-gray-700">Fetching Order List from API...</div>;
    if (error) return <div className="p-8 text-center text-xl text-red-600">🚨 Error: {error}</div>;

    return (

        <div className="p-4 sm:p-6 lg:p-8 bg-white min-h-screen">
            <h1 className="text-3xl font-bold text-gray-900 mb-6">📦 Tailoring Order List ({filteredAndSortedOrders.length} / {orders.length})</h1>

            {/* --- Search and Filter Bar --- */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg shadow-sm flex flex-wrap gap-4 items-center">

                {/* Dedicated Order ID Filter */}
                <div className="flex-1 min-w-[150px] max-w-[200px]">
                    <input
                        type="text"
                        placeholder="Filter by Order ID..."
                        value={filterOrderId}
                        onChange={(e) => setFilterOrderId(e.target.value)}
                        className="w-full p-2 border border-blue-400 rounded-md focus:ring-blue-500 focus:border-blue-500 font-semibold"
                    />
                </div>

                {/* General Search Bar */}
                <div className="flex-1 min-w-[200px]">
                    <input
                        type="text"
                        placeholder="Search: Customer, Phone, Costume, Employee, Status, Payment..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>

                {/* Status Filter Dropdown */}
                <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="p-2 border border-gray-300 rounded-md bg-white min-w-[120px]"
                >
                    {uniqueStatuses.map(status => (
                        <option key={status} value={status}>{status} Status</option>
                    ))}
                </select>

                {/* Payment Mode Filter Dropdown */}
                <select
                    value={filterPaymentMode}
                    onChange={(e) => setFilterPaymentMode(e.target.value)}
                    className="p-2 border border-gray-300 rounded-md bg-white min-w-[120px]"
                >
                    {uniquePaymentModes.map(mode => (
                        <option key={mode} value={mode}>{mode} Payment</option>
                    ))}
                </select>

                {/* Sort By Dropdown */}
                <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="p-2 border border-gray-300 rounded-md bg-white min-w-[140px]"
                >
                    <option value="order_date_desc">Latest Order Date</option>
                    <option value="order_date_asc">Oldest Order Date</option>
                </select>
            </div>
            {/* --- End Search and Filter Bar --- */}

            {filteredAndSortedOrders.length === 0 && (
                <div className="p-8 text-center text-xl text-gray-500">
                    No orders match your current filters. Try adjusting your search or filters.
                </div>
            )}

            {/* ======================================================= */}
            {/* --- 💻 Table View (Desktop/Tablet) --- */}
            {/* ======================================================= */}
            {filteredAndSortedOrders.length > 0 && (
                <div className="hidden sm:block"> {/* Hide on small screens */}
                    <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer / Order Details</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Delivery Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status / Payment</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total (₹)</th>
                                    <th className="relative px-6 py-3">
                                        <span className="sr-only">Actions</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredAndSortedOrders.map((order) => {
                                    const statusColor = getStatusColor(order.status);
                                    return (
                                        <tr key={order.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                **{order.id}**
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-bold text-gray-900">{order.customer_name}</div>
                                                <div className="text-xs text-gray-500">Phone: {order.customer_phone}</div>
                                                <div className="text-xs text-gray-500">Costume: **{order.costume_name}**</div>
                                                <div className="text-xs text-gray-500">Attended By: **{order.employee_name}**</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-semibold">
                                                {order.delivery_date}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColor} mb-1`}>
                                                    {order.status}
                                                </span>
                                                <div className="text-xs text-gray-500">Payment: {order.payment_mode}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {formatCurrency(order.total_amount)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <button
                                                    onClick={() => handleManageOrder(order.id)}
                                                    className="text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-md transition"
                                                >
                                                    Manage Order &rarr;
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            {/* --- End Table View --- */}

            {/* ======================================================= */}
            {/* --- 📱 Card View (Mobile) --- */}
            {/* ======================================================= */}
            <div className="sm:hidden space-y-4"> {/* Show only on small screens */}
                {filteredAndSortedOrders.map((order) => {
                    const statusColor = getStatusColor(order.status);

                    return (
                        <div key={order.id} className="bg-gray-50 p-4 rounded-lg shadow-md border-l-4 border-blue-600">
                            <div className="flex justify-between items-center mb-2 border-b border-dashed pb-2">
                                <h2 className="text-lg font-bold text-gray-900">Order **#{order.id}**</h2>
                                <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${statusColor}`}>
                                    {order.status}
                                </span>
                            </div>

                            <div className="space-y-1 text-sm text-gray-700">
                                <p>
                                    <span className="font-medium">Customer:</span> {order.customer_name} ({order.customer_phone})
                                </p>
                                <p>
                                    <span className="font-medium">Item:</span> **{order.costume_name}**
                                </p>
                                <p className="text-red-600 font-semibold">
                                    <span className="font-medium text-gray-700">Delivery:</span> {order.delivery_date}
                                </p>
                                <p>
                                    <span className="font-medium">Total:</span> <span className="text-base font-bold">{formatCurrency(order.total_amount)}</span>
                                </p>
                                <p>
                                    <span className="font-medium">Payment Mode:</span> {order.payment_mode}
                                </p>
                                <p>
                                    <span className="font-medium">Attended By:</span> {order.employee_name}
                                </p>
                            </div>

                            <div className="mt-4 text-right">
                                <button
                                    onClick={() => handleManageOrder(order.id)}
                                    className="text-blue-600 hover:text-blue-900 bg-blue-100 px-4 py-2 rounded-lg font-medium text-sm transition w-full"
                                >
                                    Manage Order Details &rarr;
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
            {/* --- End Card View --- */}

        </div>

    );
};

export default OrderListing;